/**
 * @file PlanningBulkEditModal.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-29
 * @modified 2026-09-29
 * @description Set haulier, driver, vehicle and collection time on several
 *              planning-board rows at once — Transport Planning and the Bag /
 *              Haulage Jobs board both open it from their bulk bar and from
 *              right-click.
 *
 *              Every field starts on "leave as is", so only what is changed
 *              changes. Choosing a driver fills in what choosing one on a row
 *              does — their sole linked haulier, their default vehicle, or the
 *              Unknown vehicle for an outside haulier's driver — here in the
 *              form, where it can be seen and overridden before applying,
 *              rather than as a cascade of hidden requests per row.
 *
 *              One request (PUT /api/transport/planning/bulk-update), one
 *              transaction on the server.
 */

import React, { useMemo, useState } from 'react';
import Modal from './Modal';
import Input from './Input';
import SearchableDropdown from './SearchableDropdown';
import { useSlideOutButton } from './SlideOutButton';
import { normalizeTimeValue } from '../utils/planningTime';
import { UNKNOWN_VEHICLE, isExcelHaulier } from '../utils/planningRows';
import './PlanningBulkEditModal.css';

const KEEP = '__keep';
const keepOption = { value: KEEP, label: '— leave as is —' };

function BulkBody({ count, jobCount, form, set, haulierOptions, driverOptions, vehicleOptions, error, busy }) {
  useSlideOutButton([busy]);
  return (
    <div className="planning-bulk-body">
      <p className="planning-bulk-intro">
        Changing {count} row{count === 1 ? '' : 's'}. Anything left as is stays as it is on each row.
        {jobCount > 0 && ` ${jobCount} ${jobCount === 1 ? 'is a haulage job' : 'are haulage jobs'} — always hauled by Excel Agri, so the haulier is not changed on ${jobCount === 1 ? 'it' : 'them'}.`}
      </p>
      {error && <div className="planning-bulk-error">{error}</div>}
      <div className="planning-bulk-grid">
        <label>Haulier</label>
        <SearchableDropdown value={form.haulier} options={haulierOptions} onChange={(v) => set('haulier', v ?? '')}
          placeholder="Haulier" searchPlaceholder="Search hauliers..." disabled={busy} />
        <label>Driver</label>
        <SearchableDropdown value={form.driver} options={driverOptions} onChange={(v) => set('driver', v ?? '')}
          placeholder="Driver" searchPlaceholder="Search drivers..." disabled={busy} />
        <label>Vehicle</label>
        <SearchableDropdown value={form.vehicle} options={vehicleOptions} onChange={(v) => set('vehicle', v ?? '')}
          placeholder="Vehicle" searchPlaceholder="Search vehicles..." disabled={busy} />
        <label>Time</label>
        <Input value={form.time} placeholder="leave as is — or e.g. 9, 14:30, 2pm"
          onChange={(e) => set('time', e.target.value)}
          onBlur={(e) => { if (e.target.value.trim()) set('time', normalizeTimeValue(e.target.value)); }}
          disabled={busy} />
      </div>
    </div>
  );
}

/**
 * @param {number}   count        rows selected
 * @param {number}   jobCount     how many of them are haulage jobs (bag board)
 * @param {string[]} hauliers     haulier names
 * @param {Array}    drivers      driver records ({ name, hauliers[], default_vehicle_registration })
 * @param {string[]} vehicles     registrations
 * @param {function} onApply      async (changes) — { haulier?, driver?, vehicle?, scheduledTime? }
 * @param {function} onClose
 */
function PlanningBulkEditModal({ count, jobCount = 0, hauliers = [], drivers = [], vehicles = [], onApply, onClose }) {
  const [form, setForm] = useState({ haulier: KEEP, driver: KEEP, vehicle: KEEP, time: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const haulierOptions = useMemo(() => [keepOption, { value: '', label: 'None' },
    ...hauliers.map(h => ({ value: h, label: h }))], [hauliers]);
  const driverOptions = useMemo(() => [keepOption, { value: '', label: 'None' },
    ...drivers.map(d => ({ value: d.name, label: d.name }))], [drivers]);
  const vehicleOptions = useMemo(() => [keepOption, { value: '', label: 'None' },
    { value: UNKNOWN_VEHICLE, label: 'Unknown (set payload)' },
    ...vehicles.map(v => ({ value: v, label: v }))], [vehicles]);

  const set = (field, value) => setForm(prev => {
    const next = { ...prev, [field]: value };
    // A driver brings what they bring on a single row — shown here, editable.
    if (field === 'driver' && value && value !== KEEP) {
      const d = drivers.find(x => x.name === value);
      const linked = d?.hauliers || [];
      if (linked.length === 1) next.haulier = linked[0].name;
      if (d?.default_vehicle_registration) next.vehicle = d.default_vehicle_registration;
      else {
        const haulier = next.haulier !== KEEP ? next.haulier : '';
        if (haulier && !isExcelHaulier(haulier)) next.vehicle = UNKNOWN_VEHICLE;
      }
    }
    return next;
  });

  const changes = {};
  if (form.haulier !== KEEP) changes.haulier = form.haulier;
  if (form.driver !== KEEP) changes.driver = form.driver;
  if (form.vehicle !== KEEP) changes.vehicle = form.vehicle;
  if (form.time.trim()) changes.scheduledTime = normalizeTimeValue(form.time);
  const nothing = !Object.keys(changes).length;

  const apply = async () => {
    setBusy(true);
    setError('');
    try {
      await onApply(changes);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={busy ? undefined : onClose}
      title={`Edit ${count} row${count === 1 ? '' : 's'}`}
      size="md"
      className="planning-bulk-edit"
      footer={
        <div className="buttons slideout">
          <div>
            <button data-read-safe className="btn-round btn-danger" onClick={onClose} disabled={busy}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Cancel</span>
            </button>
          </div>
          <div>
            <button className="btn-slideout" data-write-action onClick={apply} disabled={busy || nothing}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">{busy ? 'progress_activity' : 'done_all'}</span></div>
              <span className="btn-label">{busy ? 'Applying…' : `Apply to ${count} row${count === 1 ? '' : 's'}`}</span>
            </button>
          </div>
        </div>
      }
    >
      <BulkBody count={count} jobCount={jobCount} form={form} set={set}
        haulierOptions={haulierOptions} driverOptions={driverOptions} vehicleOptions={vehicleOptions}
        error={error} busy={busy} />
    </Modal>
  );
}

export default PlanningBulkEditModal;
